import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import InstructorCourseManagement from '../components/instructor/InstructorCourseManagement';

const InstructorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'overview' | 'manage'>('overview');

  // Allow instructors, admins, and super_admins
  if (!user || (!user.is_instructor && !user.is_admin && user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the instructor dashboard.</p>
          <p className="text-gray-500 mt-2">Only instructors and admins can access this page.</p>
        </div>
      </div>
    );
  }

  const isAdmin = user.is_admin || user.role === 'admin' || user.role === 'super_admin';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Instructor Dashboard</h1>
              <p className="text-gray-600 mt-2">Create and manage your courses</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveView('overview')}
                className={`px-4 py-2 rounded-md font-medium ${
                  activeView === 'overview'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveView('manage')}
                className={`px-4 py-2 rounded-md font-medium ${
                  activeView === 'manage'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Manage Courses
              </button>
            </div>
          </div>
        </div>

        {activeView === 'overview' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Welcome to the Instructor Dashboard</h2>
            <p className="text-gray-600 mb-6">
              {isAdmin 
                ? "As an admin, you can create and manage all courses on the platform."
                : "As an instructor, you can create and manage your own courses."
              }
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">My Courses</h3>
                <p className="text-blue-700 mb-4">View and manage your created courses.</p>
                <button 
                  onClick={() => setActiveView('manage')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Manage My Courses
                </button>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Create Course</h3>
                <p className="text-green-700 mb-4">Start creating a new course with lessons.</p>
                <p className="text-sm text-green-600 mb-3">
                  Note: {isAdmin 
                    ? "You can publish courses immediately" 
                    : "Courses will be created as drafts. Contact an administrator to publish your courses."}
                </p>
                <button 
                  onClick={() => setActiveView('manage')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Create New Course
                </button>
              </div>
              
              {isAdmin && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">Admin Tools</h3>
                  <p className="text-purple-700 mb-4">Access advanced admin features.</p>
                  <a href="/admin" className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium inline-block">
                    Admin Dashboard
                  </a>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-4">
                <a href="/courses" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">
                  Browse All Courses
                </a>
                <a href="/my-courses" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">
                  My Course Library
                </a>
                {isAdmin && (
                  <a href="/admin" className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">
                    Platform Administration
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {activeView === 'manage' && (
          <InstructorCourseManagement />
        )}
      </div>
    </div>
  );
};

export default InstructorDashboard; 